import {useEffect} from "react";
import styled, {keyframes} from "styled-components";
import {useFilters} from "context/FilterContext";
import {useToast} from "hooks";
import {useCurrentLocation} from "hooks/useCurrentLocation";
import {AutoCompleteInputVariant} from "ui/base/autoCompleteInput/AutoCompleteInputVariant";
import {defaultThemeColors} from "ui/theme";

const LocateIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11.5 21 3l-8.5 18-2-7.5z"/>
    </svg>
);

/**
 * City field for search: Google place suggestions (typed) or the visitor's
 * location ("Use my location"). Typing always works; location is optional.
 *
 * variant "inline": icon button inside the field (desktop bar).
 * variant "stacked": text button and message under the field (mobile).
 */
export const LocationField = ({variant = "inline", placeholder = "Enter the city", optionType, testId, onChange}) => {
    const {filters, handleLocationChange} = useFilters();
    const {locate, fillIfAllowed, status, message} = useCurrentLocation();
    const locating = status === "locating";

    // Desktop bar has no room for a message line: use a toast.
    useEffect(() => {
        if (variant === "inline" && message) useToast("info", message);
    }, [variant, message]);

    return (
        <Wrap onFocusCapture={fillIfAllowed}>
            <Field $inline={variant === "inline"}>
                <AutoCompleteInputVariant
                    data-testid={testId}
                    borderColor="lightBlue"
                    placeholder={locating ? "Finding your location…" : placeholder}
                    placeholderColor="txt"
                    bgColor="transparent"
                    optionType={optionType}
                    rounded
                    address={filters.location ? {label: filters.location, value: filters.location} : null}
                    onChange={(value) => {
                        handleLocationChange(value);
                        onChange?.(value);
                    }}
                />
                {variant === "inline" && (
                    <IconButton
                        type="button"
                        onClick={locate}
                        disabled={locating}
                        aria-label="Use my location"
                        title="Use my location"
                        $busy={locating}
                    >
                        <LocateIcon/>
                    </IconButton>
                )}
            </Field>
            {variant === "stacked" && (
                <Below>
                    <TextButton type="button" onClick={locate} disabled={locating}>
                        <LocateIcon/>
                        {locating ? "Finding your location…" : "Use my location"}
                    </TextButton>
                    {message && <Message role="status">{message}</Message>}
                </Below>
            )}
        </Wrap>
    );
};

const pulse = keyframes`
    50% { opacity: 0.35; }
`;

const Wrap = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Field = styled.div`
    position: relative;
    width: 100%;

    ${({$inline}) => $inline && `
        label { padding-right: 40px; }
    `}
`;

const IconButton = styled.button`
    position: absolute;
    top: 50%;
    right: 6px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: ${defaultThemeColors.blue};
    cursor: pointer;
    animation: ${({$busy}) => ($busy ? pulse : "none")} 1s ease-in-out infinite;

    &:hover:not(:disabled), &:focus-visible {
        background: rgba(85, 77, 241, 0.1);
        outline: none;
    }
`;

const Below = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-left: 4px;
`;

const TextButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    color: ${defaultThemeColors.blue};
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Message = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${defaultThemeColors.txt};
    opacity: 0.7;
`;
